import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import TenantHealthModal from "@/components/TenantHealthModal";
import NudgePicker from "@/components/NudgePicker";
import HealthDriftHeatmap from "@/components/HealthDriftHeatmap";
import DriftAlertHistory from "@/components/DriftAlertHistory";
import InviteQrModal from "@/components/InviteQrModal";
import { Star, AlertCircle, CheckCircle2, XCircle, Clock, Eye, Send, Loader2, MessageCircle, Users, ChevronDown, ChevronRight, Link2, QrCode } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const relTime = (iso) => {
  if (!iso) return "never";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// Health-score → color band. 80+ green, 50-79 amber, <50 red.
const healthBand = (s) => {
  if (s === null || s === undefined) return { label: "—", bg: "bg-muted", fg: "text-muted-foreground" };
  if (s >= 80) return { label: `${s}`, bg: "bg-emerald-500/15 border border-emerald-500/40", fg: "text-emerald-700" };
  if (s >= 50) return { label: `${s}`, bg: "bg-amber-500/15 border border-amber-500/40", fg: "text-amber-700" };
  return { label: `${s}`, bg: "bg-red-500/15 border border-red-500/40", fg: "text-red-700" };
};

export default function PlatformAdminPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const [healthDrilldown, setHealthDrilldown] = useState(null);
  const [nudgeTarget, setNudgeTarget] = useState(null); // {tenantId, tenantName}
  const [waitlist, setWaitlist] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [invitingEmail, setInvitingEmail] = useState(null);
  const [qrTarget, setQrTarget] = useState(null); // {url, dealership, email}
  const [bestLine, setBestLine] = useState(null); // top-performing template

  useEffect(() => {
    api.get("/platform/tenants")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Access denied"))
      .finally(() => setLoading(false));
    api.get("/platform/waitlist").then((r) => setWaitlist(r.data)).catch(() => {});
    api.get("/platform/nudge/analytics?days=60").then((r) => {
      const rows = (r.data?.rows || []).filter((row) => row.sent >= 3); // need a minimum sample
      if (rows.length > 0) setBestLine(rows[0]); // API already sorts by reply_rate desc
    }).catch(() => {});
  }, []);

  const sendDigestNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/platform/digest/send");
      const okCount = r.data.sent.filter((s) => s.ok).length;
      toast.success(`Digest sent to ${okCount} platform admin${okCount === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest");
    } finally { setSending(false); }
  };

  if (loading) return <DashboardShell><div className="p-8 text-muted-foreground">Loading tenant snapshot…</div></DashboardShell>;
  if (err) return <DashboardShell><div className="p-8"><Card className="p-6 max-w-lg"><div className="flex items-center gap-2 text-red-600"><AlertCircle className="w-5 h-5" /> {err}</div></Card></div></DashboardShell>;

  const t = data?.totals || {};
  const rows = data?.tenants || [];

  return (
    <DashboardShell>
      <div className="p-6 max-w-7xl">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-[hsl(var(--gold))]" strokeWidth={2} />
              <div className="font-mono-label text-[hsl(var(--gold))] text-[0.65rem] tracking-widest">FOUNDING FOUR · PLATFORM VIEW</div>
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tight text-navy" data-testid="platform-heading">Every dealer, one screen</h1>
            <p className="text-sm text-muted-foreground mt-1">
              MRR, health score, activity, and last-heard-from timestamps across every tenant so you can spot who needs a nudge before they churn.
            </p>
            {bestLine && (
              <a
                href="/platform/nudge-templates"
                className="inline-flex items-center gap-2 mt-3 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-xs hover:bg-emerald-500/15 transition-colors"
                data-testid="platform-best-line-chip"
                title="Highest reply-rate nudge template in the last 60 days — click to open the analytics view"
              >
                <span className="font-mono-label text-emerald-800 text-[0.6rem] tracking-widest">BEST LINE</span>
                <span className="font-semibold text-navy">{bestLine.label}</span>
                <span className="text-emerald-800 font-bold tabular-nums">{Math.round(bestLine.reply_rate * 100)}%</span>
                <span className="text-muted-foreground text-[0.65rem]">· {bestLine.replied}/{bestLine.sent} replied</span>
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            <a href="/platform/nudge-templates">
              <Button variant="outline" size="sm" data-testid="platform-templates-btn">
                <MessageCircle className="w-4 h-4 mr-1.5" /> Templates
              </Button>
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/platform/digest/preview`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="sm" data-testid="platform-preview-digest-btn">
                <Eye className="w-4 h-4 mr-1.5" /> Preview digest
              </Button>
            </a>
            <Button onClick={sendDigestNow} disabled={sending} className="bg-navy hover:bg-navy/85" size="sm" data-testid="platform-send-digest-btn">
              {sending ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</>) : (<><Send className="w-4 h-4 mr-1.5" /> Send digest now</>)}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 mt-6">
          <Card className="p-5" data-testid="platform-kpi-mrr">
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">MRR</div>
            <div className="font-display text-3xl font-bold text-navy mt-1 tabular-nums">{money(t.total_mrr)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">across {t.active_tenants || 0} paying dealers</div>
          </Card>
          <Card className="p-5" data-testid="platform-kpi-arr">
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">ARR (projected)</div>
            <div className="font-display text-3xl font-bold text-emerald-600 mt-1 tabular-nums">{money(t.arr_projected)}</div>
            <div className="text-xs text-muted-foreground mt-0.5">if today's roster stays put</div>
          </Card>
          <Card className="p-5" data-testid="platform-kpi-active">
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">Active tenants</div>
            <div className="font-display text-3xl font-bold tabular-nums text-navy mt-1">{t.active_tenants || 0} <span className="text-lg text-muted-foreground">/ 4</span></div>
            <div className="text-xs text-muted-foreground mt-0.5">founding-dealer seats claimed</div>
          </Card>
          <Card className="p-5" data-testid="platform-kpi-pending">
            <div className="font-mono-label text-muted-foreground text-[0.6rem]">Pending signups</div>
            <div className="font-display text-3xl font-bold tabular-nums text-amber-600 mt-1">{t.pending_tenants || 0}</div>
            <div className="text-xs text-muted-foreground mt-0.5">signed up · haven't paid</div>
          </Card>
        </div>

        <HealthDriftHeatmap />

        <Card className="overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <div className="font-display text-lg font-bold">Tenant roster</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="platform-tenants-table">
              <thead className="bg-muted/50 text-[0.65rem] font-mono-label tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2.5">Dealer</th>
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-center px-3 py-2.5">Health</th>
                  <th className="text-right px-3 py-2.5">MRR</th>
                  <th className="text-right px-3 py-2.5">Declines</th>
                  <th className="text-right px-3 py-2.5">SMS<br/>30d</th>
                  <th className="text-right px-3 py-2.5">Revenue<br/>30d</th>
                  <th className="text-left px-3 py-2.5">Twilio #</th>
                  <th className="text-left px-3 py-2.5">Last activity</th>
                  <th className="text-left px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => {
                  const stale = r.last_activity_at && (Date.now() - new Date(r.last_activity_at).getTime()) > 7 * 86400 * 1000;
                  const hb = healthBand(r.health_score);
                  return (
                    <tr key={r.id} className="hover:bg-muted/20" data-testid={`platform-tenant-row-${r.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-navy">{r.name}</div>
                        <div className="text-[0.65rem] font-mono text-muted-foreground">{r.id} · {r.user_count} user{r.user_count === 1 ? "" : "s"}</div>
                      </td>
                      <td className="px-3 py-3">
                        {r.active ? (
                          <Badge className="bg-emerald-500/10 text-emerald-700 border border-emerald-500/40 hover:bg-emerald-500/10">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                          </Badge>
                        ) : r.cancelled_at ? (
                          <Badge className="bg-red-500/10 text-red-700 border border-red-500/40 hover:bg-red-500/10">
                            <XCircle className="w-3 h-3 mr-1" /> Cancelled
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/10 text-amber-700 border border-amber-500/40 hover:bg-amber-500/10">
                            <Clock className="w-3 h-3 mr-1" /> Pending
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setHealthDrilldown({ tenantId: r.id, tenantName: r.name, score: r.health_score })}
                          disabled={r.health_score === null}
                          className={`inline-flex items-center justify-center w-11 h-11 rounded-full font-bold text-sm tabular-nums transition-transform hover:scale-110 ${hb.bg} ${hb.fg} ${r.health_score === null ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                          data-testid={`platform-health-${r.id}`}
                          title={r.health_score === null ? "Not scored — pending activation" : `Click for 30-day drill-down`}
                        >
                          {hb.label}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-semibold">{money(r.mrr)}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{r.decline_count}</td>
                      <td className="px-3 py-3 text-right tabular-nums">{r.sms_last_30d}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-emerald-700">{money(r.revenue_recovered_30d)}</td>
                      <td className="px-3 py-3 font-mono text-xs">
                        {r.twilio_from_number ? (
                          <span className="text-navy">{r.twilio_from_number}</span>
                        ) : (
                          <span className="text-muted-foreground">— shared —</span>
                        )}
                      </td>
                      <td className={`px-3 py-3 text-xs ${stale ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>
                        {relTime(r.last_activity_at)}
                        {stale && <span className="ml-1">🚨</span>}
                      </td>
                      <td className="px-3 py-3">
                        {r.active && (stale || (r.health_score !== null && r.health_score < 70)) && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setNudgeTarget({ tenantId: r.id, tenantName: r.name })}
                              data-testid={`platform-nudge-${r.id}`}
                              className="text-xs h-8"
                              title="Pick a template + fire a check-in SMS to this dealer's admin"
                            >
                              <MessageCircle className="w-3.5 h-3.5 mr-1" /> Nudge
                            </Button>
                            {r.nudges_last_7d > 0 && (
                              <span
                                className="text-[0.6rem] font-mono-label tracking-widest px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-800 border border-amber-500/40"
                                data-testid={`platform-nudge-count-${r.id}`}
                                title={`${r.nudges_last_7d} nudge${r.nudges_last_7d === 1 ? "" : "s"} sent in the last 7 days`}
                              >
                                {r.nudges_last_7d}×
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="px-4 py-12 text-center text-muted-foreground">No tenants yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="text-[0.7rem] text-muted-foreground mt-4">
          Health score (0-100): 30 pts SMS activity + 30 pts revenue recovered + 20 pts appointment velocity + 20 pts recency. Green &ge;80 · amber 50-79 · red &lt;50. Pending tenants aren't scored. Click any score for a 30-day drill-down.
        </div>

        {/* Waitlist collapsible section */}
        {waitlist && (
          <Card className="mt-6 overflow-hidden" data-testid="platform-waitlist-section">
            <button
              type="button"
              onClick={() => setWaitlistOpen((o) => !o)}
              className="w-full px-5 py-3 border-b border-border bg-muted/30 flex items-center justify-between hover:bg-muted/50 transition-colors"
              data-testid="platform-waitlist-toggle"
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div className="font-display text-lg font-bold">Sold-out waitlist</div>
                <span className="text-xs bg-amber-500/15 text-amber-700 border border-amber-500/40 rounded-full px-2 py-0.5 font-mono-label tracking-widest">
                  {waitlist.count} waiting
                </span>
              </div>
              {waitlistOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {waitlistOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="platform-waitlist-table">
                  <thead className="bg-muted/20 text-[0.65rem] font-mono-label tracking-widest text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2.5">Dealership</th>
                      <th className="text-left px-3 py-2.5">Contact</th>
                      <th className="text-left px-3 py-2.5">Email</th>
                      <th className="text-left px-3 py-2.5">Phone</th>
                      <th className="text-left px-3 py-2.5">State</th>
                      <th className="text-left px-3 py-2.5">Joined</th>
                      <th className="text-left px-3 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(waitlist.waitlist || []).map((w) => (
                      <tr key={w.admin_email} className="hover:bg-muted/20" data-testid={`platform-waitlist-row-${w.admin_email.replace(/[^a-z0-9]/gi, "-")}`}>
                        <td className="px-4 py-3 font-semibold text-navy">{w.dealership_name}</td>
                        <td className="px-3 py-3">{w.admin_name || <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-3 font-mono text-xs">
                          <a href={`mailto:${w.admin_email}`} className="text-navy hover:underline">{w.admin_email}</a>
                        </td>
                        <td className="px-3 py-3 font-mono text-xs">
                          {w.dealership_phone ? <a href={`tel:${w.dealership_phone}`} className="text-navy hover:underline">{w.dealership_phone}</a> : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-3 uppercase font-mono text-xs">{w.dealership_state || <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-3 text-xs text-muted-foreground">{relTime(w.joined_at)}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant={w.invited_at ? "outline" : "default"}
                              className={w.invited_at ? "text-xs h-8" : "text-xs h-8 bg-navy hover:bg-navy/85"}
                              disabled={invitingEmail === w.admin_email}
                              onClick={async () => {
                                setInvitingEmail(w.admin_email);
                                try {
                                  await api.post(`/platform/waitlist/${encodeURIComponent(w.admin_email)}/invite`, {});
                                  toast.success(`Invite sent to ${w.admin_email}`);
                                  const refresh = await api.get("/platform/waitlist");
                                  setWaitlist(refresh.data);
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || "Invite failed");
                                } finally { setInvitingEmail(null); }
                              }}
                              data-testid={`platform-waitlist-invite-${w.admin_email.replace(/[^a-z0-9]/gi, "-")}`}
                            >
                            {invitingEmail === w.admin_email ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (w.invited_at ? "Re-invite" : <><Send className="w-3.5 h-3.5 mr-1" /> Invite</>)}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-8"
                            disabled={invitingEmail === w.admin_email + ":copy"}
                            title={w.invite_token ? "Copy invite signup URL" : "Fires the invite first, then copies the URL"}
                            data-testid={`platform-waitlist-copy-${w.admin_email.replace(/[^a-z0-9]/gi, "-")}`}
                            onClick={async () => {
                              setInvitingEmail(w.admin_email + ":copy");
                              try {
                                let token = w.invite_token;
                                if (!token) {
                                  const r = await api.post(`/platform/waitlist/${encodeURIComponent(w.admin_email)}/invite`, {});
                                  token = r.data.invite_token;
                                  const refresh = await api.get("/platform/waitlist");
                                  setWaitlist(refresh.data);
                                }
                                const url = `${window.location.origin}/signup?token=${token}`;
                                await navigator.clipboard.writeText(url);
                                toast.success("Invite link copied — paste it anywhere");
                              } catch (e) {
                                toast.error(e.response?.data?.detail || "Couldn't copy invite link");
                              } finally { setInvitingEmail(null); }
                            }}
                          >
                            <Link2 className="w-3.5 h-3.5 mr-1" /> Copy link
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-8"
                            disabled={invitingEmail === w.admin_email + ":qr"}
                            title={w.invite_token ? "Show scannable QR of the invite link" : "Fires the invite first, then shows the QR"}
                            data-testid={`platform-waitlist-qr-${w.admin_email.replace(/[^a-z0-9]/gi, "-")}`}
                            onClick={async () => {
                              setInvitingEmail(w.admin_email + ":qr");
                              try {
                                let token = w.invite_token;
                                if (!token) {
                                  const r = await api.post(`/platform/waitlist/${encodeURIComponent(w.admin_email)}/invite`, {});
                                  token = r.data.invite_token;
                                  const refresh = await api.get("/platform/waitlist");
                                  setWaitlist(refresh.data);
                                }
                                setQrTarget({
                                  url: `${window.location.origin}/signup?token=${token}`,
                                  dealership: w.dealership_name,
                                  email: w.admin_email,
                                });
                              } catch (e) {
                                toast.error(e.response?.data?.detail || "Couldn't generate QR");
                              } finally { setInvitingEmail(null); }
                            }}
                          >
                            <QrCode className="w-3.5 h-3.5 mr-1" /> QR
                          </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(!waitlist.waitlist || waitlist.waitlist.length === 0) && (
                      <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">Nobody on the waitlist yet — good news, seats are still open.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        <DriftAlertHistory />
      </div>
      <TenantHealthModal
        tenantId={healthDrilldown?.tenantId}
        tenantName={healthDrilldown?.tenantName}
        healthScore={healthDrilldown?.score}
        onOpenChange={(open) => !open && setHealthDrilldown(null)}
      />
      {nudgeTarget && (
        <NudgePicker
          tenantId={nudgeTarget.tenantId}
          tenantName={nudgeTarget.tenantName}
          open={!!nudgeTarget}
          onOpenChange={(open) => !open && setNudgeTarget(null)}
        />
      )}
      <InviteQrModal
        url={qrTarget?.url}
        dealership={qrTarget?.dealership}
        email={qrTarget?.email}
        open={!!qrTarget}
        onOpenChange={(open) => !open && setQrTarget(null)}
      />
    </DashboardShell>
  );
}
